import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { isValidId } from '../u2000Core/tools/U2000IdUtils';
import { doGetData as doGetCustomerData, type U3120Customer } from '../u3120Customer/U3120Service';
import { NS } from './U3121';
import {
  doGetData as doGetProjectData,
  luHandleAppiError,
  type U3121Project,
  type U3121ProjectToSurvey,
} from './U3121Service';
import { hasUsableCustomerAddress, u3121MapCustomerToPtsAddress } from './tools/u3121MapCustomerToPtsAddress';

export type U3121CopyCustomerAddressToPtsParams = {
  customerId?: string | null;
  parentProjectId?: string | null;
};

export type U3121CopyCustomerAddressToPtsResult = Pick<
  U3121ProjectToSurvey,
  'owner' | 'address' | 'city' | 'postalCode' | 'country' | 'state'
> | null;

function unwrapCustomer(raw: unknown): U3120Customer | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // GET typique : { result: customer } ou customer à la racine
  if (r.result && typeof r.result === 'object') return r.result as U3120Customer;
  if (typeof r.id === 'string') return raw as U3120Customer;
  return null;
}

function unwrapProject(raw: unknown): U3121Project | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.result && typeof r.result === 'object') return r.result as U3121Project;
  if (typeof r.id === 'string') return raw as U3121Project;
  return null;
}

/** Copie nom/adresse client U3120 vers les champs PTS (BL-0436). */
export function useU3121CopyCustomerAddressToPtsHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const handleError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
    },
    [t, toast],
  );

  const resolveCustomerId = useCallback(
    async (params: U3121CopyCustomerAddressToPtsParams): Promise<string | null> => {
      const fromCustomer = params.customerId?.trim();
      if (fromCustomer && isValidId(fromCustomer)) return fromCustomer;

      const parentProjectId = params.parentProjectId?.trim();
      if (!parentProjectId || !isValidId(parentProjectId)) return null;

      try {
        const response = await doGetProjectData(axios, parentProjectId);
        const project = unwrapProject(response.data);
        const customerId = project?.customer?.id?.trim();
        if (!customerId || !isValidId(customerId)) {
          toast.warning(t('msgCopyCustomerAddressNoCustomer', { ns: NS }));
          return null;
        }
        return customerId;
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError, t, toast],
  );

  const copyToPts = useCallback(
    async (params: U3121CopyCustomerAddressToPtsParams): Promise<U3121CopyCustomerAddressToPtsResult> => {
      const customerId = await resolveCustomerId(params);
      if (!customerId) {
        if (!params.customerId?.trim() && !params.parentProjectId?.trim()) {
          toast.warning(t('msgCopyCustomerAddressNoCustomer', { ns: NS }));
        }
        return null;
      }

      try {
        const response = await doGetCustomerData(axios, customerId);
        const customer = unwrapCustomer(response.data);
        if (!customer || !hasUsableCustomerAddress(customer)) {
          toast.warning(t('msgCopyCustomerAddressEmpty', { ns: NS }));
          return null;
        }
        return u3121MapCustomerToPtsAddress(customer);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError, resolveCustomerId, t, toast],
  );

  return { copyToPts };
}
